import { Box, Button, Stack, Typography } from '@mui/material'
import { motion } from 'framer-motion'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'
import { SlideDefinition } from '../types'
import COLORS from '../../../styles/colors'
import {
  parseDelayMs,
  parseNumber,
  parsePercentString,
  parsePixelNumber,
} from './utils/slideParsing'

type BookshelfSlideProps = {
  isActive: boolean
  slide: SlideDefinition
  locale?: string
  isExpanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
}

const LOCALIZED_IMAGE_SRC = {
  da: '/landing-page/bookshelf_fullpage_da.webp',
  en: '/landing-page/bookshelf_fullpage.webp',
} as const

const DEFAULT_IMAGE_ALT = 'Bookshelf screenshot'
const DEFAULT_MERCH_URL =
  'https://www.merch.humanlibrary.org/product-page/human-library-card-w-3-book-plan'
const DEFAULT_PAN_DISTANCE = '-50%'
const DEFAULT_START_OFFSET_PX = 0
const DEFAULT_PAN_DISTANCE_PX = 260
const DEFAULT_PAN_START_DELAY_MS = 500
const DEFAULT_START_OFFSET_PERCENT = 0
const DEFAULT_PAN_DISTANCE_PERCENT = 100

const formatOffset = (value: number, unit: '%' | 'px') => `-${value}${unit}`

const BookshelfSlide = ({
  isActive,
  slide,
  locale,
  isExpanded = false,
  onExpandedChange,
}: BookshelfSlideProps) => {
  const { t } = useTranslation('HeroCarousel')
  const normalizedLocale = locale === 'da' ? 'da' : 'en'
  const imageSrc = LOCALIZED_IMAGE_SRC[normalizedLocale]
  const imageAlt = t('bookshelf.imageAlt', {
    defaultValue: DEFAULT_IMAGE_ALT,
    lng: normalizedLocale,
  })
  const title = t('bookshelf.title', {
    defaultValue: slide.title,
    lng: normalizedLocale,
  })
  const merchUrl = t('bookshelf.merchUrl', {
    defaultValue: DEFAULT_MERCH_URL,
    lng: normalizedLocale,
  })
  const merchLinkText = t('bookshelf.merchLinkText', {
    defaultValue: 'Visit our merch store',
    lng: normalizedLocale,
  })
  const description = t('bookshelf.description', {
    defaultValue: slide.description,
    lng: normalizedLocale,
  })
  const readMoreLabel = t('bookshelf.readMore', {
    defaultValue: 'Read more',
    lng: normalizedLocale,
  })
  const showLessLabel = t('bookshelf.showLess', {
    defaultValue: 'Show less',
    lng: normalizedLocale,
  })
  const callout = t('bookshelf.callout', {
    defaultValue: 'Interested in becoming a Reader on Human Library Online?',
    lng: normalizedLocale,
  })

  const descriptionRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const [hasOverflow, setHasOverflow] = useState(false)
  const [containerHeight, setContainerHeight] = useState(0)
  const [imageHeight, setImageHeight] = useState(0)
  useEffect(() => {
    if (isExpanded) return
    const el = descriptionRef.current
    if (!el) return
    const check = () => setHasOverflow(el.scrollHeight > el.clientHeight + 1)
    check()
    const observer = new ResizeObserver(check)
    observer.observe(el)
    return () => observer.disconnect()
  }, [description, isExpanded])

  useEffect(() => {
    const containerEl = containerRef.current
    const imageEl = imgRef.current
    if (!containerEl || !imageEl) return
    const update = () => {
      setContainerHeight(containerEl.clientHeight)
      setImageHeight(imageEl.clientHeight)
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(containerEl)
    observer.observe(imageEl)
    return () => observer.disconnect()
  }, [])

  const measuredEndY =
    imageHeight > containerHeight ? -(imageHeight - containerHeight) : 0

  const panDistance =
    typeof slide.data?.panDistance === 'string'
      ? slide.data.panDistance
      : DEFAULT_PAN_DISTANCE

  const startOffsetPx = parsePixelNumber(
    slide.data?.startOffsetPx,
    DEFAULT_START_OFFSET_PX,
  )

  const panDistancePx = parsePixelNumber(
    slide.data?.panDistancePx,
    DEFAULT_PAN_DISTANCE_PX,
  )

  const panStartDelayMs = parseDelayMs(
    slide.data?.panStartDelayMs,
    DEFAULT_PAN_START_DELAY_MS,
  )

  const startOffsetPercent = Math.max(
    0,
    parseNumber(slide.data?.startOffsetPercent, DEFAULT_START_OFFSET_PERCENT),
  )

  const panDistancePercent = Math.max(
    0,
    parseNumber(slide.data?.panDistancePercent, DEFAULT_PAN_DISTANCE_PERCENT),
  )

  const hasPercentPanConfig =
    slide.data?.startOffsetPercent !== undefined ||
    slide.data?.panDistancePercent !== undefined

  const hasPixelPanConfig =
    slide.data?.startOffsetPx !== undefined ||
    slide.data?.panDistancePx !== undefined

  // Legacy fallback: percentage travel behaves as viewport-relative movement.
  const panPercent = parsePercentString(panDistance, 28)
  const fallbackStartOffsetPx = 0
  const fallbackTravelPx = panPercent

  const panConfig = hasPercentPanConfig
    ? {
        unit: '%' as const,
        start: startOffsetPercent,
        end: startOffsetPercent + panDistancePercent,
      }
    : hasPixelPanConfig
      ? {
          unit: 'px' as const,
          start: startOffsetPx,
          end: startOffsetPx + panDistancePx,
        }
      : {
          unit: '%' as const,
          start: fallbackStartOffsetPx,
          end: fallbackStartOffsetPx + fallbackTravelPx,
        }

  const initialY = formatOffset(panConfig.start, panConfig.unit)
  const activeY = formatOffset(panConfig.end, panConfig.unit)

  return (
    <Stack
      direction="row"
      sx={{
        width: '100%',
        height: isExpanded ? 'auto' : '100%',
        position: 'relative',
        overflow: isExpanded ? 'visible' : 'hidden',
        background: slide.background,
        opacity: 1,
      }}
    >
      <Stack
        spacing={1.5}
        sx={{
          flex: { xs: '', sm: '0 0 clamp(300px, 50%, 550px)' },
          height: isExpanded ? 'auto' : '100%',
          minWidth: 0,
          px: { xs: 2.5, md: 4 },
          py: { xs: 2.25, md: 3 },
          pl: { xs: 2.5, md: 4, lg: 8 },
          pb: { xs: 5.25 },
          justifyContent: 'flex-start',
          color: COLORS.greyDark,
          background: {
            xs: `linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), url(${imageSrc}) no-repeat center center / cover`,
            sm: `linear-gradient(180deg, ${COLORS.beige} 0%, #f4e6d6 100%)`,
          },
        }}
      >
        <Typography
          variant="garage_h2"
          sx={{
            maxWidth: '100%',
            fontSize: {
              xs: '3.75rem',
              sm: '3.75rem',
              md: '4.75rem',
              lg: '5.25rem',
            },
            lineHeight: 1.15,
          }}
        >
          {title}
        </Typography>
        <Box
          ref={descriptionRef}
          sx={{
            minHeight: 0,
            overflow: 'hidden',
            maxHeight: isExpanded
              ? 'none'
              : { xs: '7.5em', sm: '9em', md: '12em', lg: 'none' },
          }}
        >
          <Typography
            variant="inter_smallText"
            sx={{
              opacity: 0.95,
              maxWidth: '100%',
              whiteSpace: 'pre-line',
            }}
          >
            {description}
          </Typography>
        </Box>
        {(hasOverflow || isExpanded) && (
          <Button
            variant="text"
            onClick={(event) => {
              event.stopPropagation()
              onExpandedChange?.(!isExpanded)
            }}
            sx={{
              alignSelf: 'flex-start',
              px: 0,
              minWidth: 0,
              color: COLORS.greyDark,
              textTransform: 'none',
              textDecoration: 'underline',
              textUnderlineOffset: '2px',
              display: { xs: 'inline-flex', lg: 'none' },
            }}
          >
            {isExpanded ? showLessLabel : readMoreLabel}
          </Button>
        )}
        <Typography
          variant="inter_smallText"
          sx={{
            opacity: 0.95,
            maxWidth: '100%',
            fontWeight: 600,
            whiteSpace: 'pre-line',
          }}
        >
          {callout}
        </Typography>
        {locale !== 'da' && (
          <Button
            variant="contained"
            href={merchUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            sx={{
              backgroundColor: COLORS.burnedOrange,
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: COLORS.burnedOrange,
              },
            }}
          >
            {merchLinkText}
          </Button>
        )}
      </Stack>

      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          flex: 1,
          minWidth: 0,
          minHeight: { xs: '320px', md: '480px' },
          overflow: 'hidden',
        }}
      >
        <motion.img
          ref={imgRef}
          src={imageSrc}
          alt={imageAlt}
          initial={{ y: 0, scale: 1.03 }}
          animate={
            isActive ? { y: measuredEndY, scale: 1 } : { y: 0, scale: 1.03 }
          }
          transition={{
            duration: slide.durationMs / 1000,
            delay: panStartDelayMs / 1000,
            ease: 'easeOut',
          }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: 'auto',
            display: 'block',
            pointerEvents: 'none',
            transformOrigin: 'center top',
          }}
        />
      </Box>
    </Stack>
  )
}

export default BookshelfSlide
